import { Components, Theme } from '@mui/material/styles';

/**
 * Card Component Theming
 *
 * Based exactly on Portal Pixels Figma design system:
 * https://www.figma.com/design/RcTf0wSf31Elw7uY8VL6rH/Portal-Pixels?node-id=1-12126&p=f&m=dev
 *
 * Only overrides styles that differ from themeDefault.tsx
 * Rest will be inherited from base theme
 */

/**
 * Create Card Component Theme
 */
export const createCardTheme = (theme: Theme): Components['MuiCard'] => {
  return {
    defaultProps: {
      raised: false,
      elevation: 0,
    },
    styleOverrides: {
      root: {
        padding: '24px',
        borderRadius: 12,
        border: `1px solid ${
          theme.palette.border?.dark ?? theme.palette.divider
        }`,
        backgroundColor: theme.palette.background.primary,
      },
    },
    variants: [
      {
        props: {
          variant: 'selected',
        },
        style: ({ theme }: any) => ({
          padding: 0,
          border: `1px solid ${theme.palette.secondary.main}`,
        }),
      },
      {
        props: {
          variant: 'contained',
        },
        style: {
          padding: 0,
        },
      },
    ],
  };
};

/**
 * Create CardHeader Component Theme
 */
export const createCardHeaderTheme = (
  theme: Theme,
): Components['MuiCardHeader'] => {
  return {
    defaultProps: {
      titleTypographyProps: {
        variant: 'h3',
      },
    },
    styleOverrides: {
      title: {
        color: theme.palette.text.primary,
        lineHeight: '24px',
      },
      subheader: {
        color: theme.palette.text.secondary,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '20px',
      },
      action: {
        marginRight: theme.spacing(0),
      },
      root: {
        padding: 0,
      },
    },
  };
};

/**
 * Create CardContent Component Theme
 */
export const createCardContentTheme = (
  _theme: Theme,
  textColor: 'primary' | 'secondary' = 'secondary',
): Components['MuiCardContent'] => {
  return {
    styleOverrides: {
      root: ({ theme }) => {
        const runtimeTheme = theme as Theme;

        return {
          paddingTop: runtimeTheme.spacing(3),
          paddingLeft: 0,
          paddingRight: 0,
          lineHeight: '20px',
          color: runtimeTheme.palette.text[textColor],
          '&:last-child': {
            paddingBottom: 0,
          },
        };
      },
    },
  };
};

export default createCardTheme;

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    selected: true;
    contained: true;
  }
}
